/**
 * Selection helpers for uploaded source files rendered as PM blocks / transcript lines.
 * Corpus documents use #segment-{n}; source files use [data-block-index].
 */

export function getBlockElementFromNode(contentRoot, node) {
    if (!contentRoot || !node) return null;
    let el = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
    while (el && el !== contentRoot) {
        if (el.nodeType === 1 && el.dataset?.blockIndex != null) {
            return el;
        }
        el = el.parentElement;
    }
    return null;
}

export function parseBlockIndex(blockEl) {
    if (!blockEl?.dataset) return null;
    const value = Number(blockEl.dataset.blockIndex);
    return Number.isInteger(value) ? value : null;
}

export function getOffsetWithinBlock(blockEl, node, nodeOffset) {
    if (!blockEl || !node || typeof nodeOffset !== 'number') return 0;
    try {
        const range = document.createRange();
        range.setStart(blockEl, 0);
        range.setEnd(node, nodeOffset);
        return range.toString().length;
    } catch {
        return 0;
    }
}

export function getBlockTextLength(blockEl) {
    return blockEl?.textContent?.length ?? 0;
}

/** Normalize a selection to PM block indices + char offsets within blocks. */
export function computeBlockSelection(range, contentElement) {
    if (!range || !contentElement) return null;

    const startBlockEl = getBlockElementFromNode(contentElement, range.startContainer);
    const endBlockEl = getBlockElementFromNode(contentElement, range.endContainer);
    const startIndex = parseBlockIndex(startBlockEl);
    const endIndex = parseBlockIndex(endBlockEl);
    if (startIndex == null || endIndex == null) return null;

    const startOffset = getOffsetWithinBlock(startBlockEl, range.startContainer, range.startOffset);
    let endOffset = getOffsetWithinBlock(endBlockEl, range.endContainer, range.endOffset);

    if (endIndex > startIndex && endOffset === 0) {
        const prevIndex = endIndex - 1;
        const prevEl = contentElement.querySelector(`[data-block-index="${prevIndex}"]`);
        if (prevEl) {
            return {
                startIndex,
                startOffset,
                endIndex: prevIndex,
                endOffset: getBlockTextLength(prevEl),
            };
        }
    }

    return {startIndex, startOffset, endIndex, endOffset};
}

export function buildSourceFileSelectionSnippet(range, contentElement, {
    referenceId,
    docTitle,
    docUrl = null,
    segmentsByBlockIndex = null,
    toCanonicalBlockIndex = null,
} = {}) {
    if (!range || !referenceId) return null;
    const selectedText = range.toString();
    if (!selectedText || !selectedText.trim()) return null;

    const norm = computeBlockSelection(range, contentElement);
    if (!norm) return null;

    let {startIndex, startOffset, endIndex, endOffset} = norm;
    if (typeof toCanonicalBlockIndex === 'function') {
        startIndex = toCanonicalBlockIndex(startIndex) ?? startIndex;
        endIndex = toCanonicalBlockIndex(endIndex) ?? endIndex;
    }

    const startSegId = segmentsByBlockIndex?.[startIndex]
        ?? segmentsByBlockIndex?.[Object.keys(segmentsByBlockIndex || {})[0]]
        ?? startIndex;

    return {
        docId: referenceId,
        docUrl,
        docTitle,
        content: selectedText,
        segmentId: startSegId,
        index: startIndex,
        offset: startOffset,
        localId: [referenceId, startIndex, startOffset, endIndex, endOffset].join(':'),
        start: {segmentIndex: startIndex, segmentOffset: startOffset},
        end: {segmentIndex: endIndex, segmentOffset: endOffset},
        tags: [],
    };
}
